import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
    Building2,
    Mail,
    Globe,
    Phone,
    MapPin,
    Briefcase,
    Plus,
    Package,
    Loader2,
    Megaphone,
    CalendarDays,
    ShieldCheck,
    Crown,
    CalendarClock,
    Users,
    IndianRupee,
    Target,
    UserPlus,
    Rocket,
    Bot,
    History,
    Sparkles,
    TrendingUp,
    AlertTriangle,
    Lightbulb,
} from "lucide-react";
import Starfield from "./Starfield";
import Sidebar from "./company_Dashboard_sidebar";
// Moved here from company_product_dashboard.js — Business Workspace now
// lives on the company Home page instead of inside the /app product dashboard.
import BusinessWorkspaceSection from "./Businessworkspacesection";
// Same shared API config every other page (e.g. SalesPipelineSection.js)
// already uses — GROWTHOS_BASE already points at the /growthos-mounted
// main.py app (local dev via local_dev_app.py, prod per src/config/api.js),
// so endpoints built from it never need a hand-rolled prefix here.
import { GROWTHOS_BASE, authHeaders } from "../config/api";


/* ==========================================================
   HOME PAGE — Company Dashboard, shown right after login.
   Same company (isfathena.company_registrations) + products
   (isfathena.products) data and CRUD as before — only the
   layout changed, into: hero, stats, quick actions, products,
   recent activity, AI insights. Sidebar/routing untouched.
========================================================== */

const COMPANY_ENDPOINT = `${GROWTHOS_BASE}/auth/me`;
// Overview stats (including Products count), Recent Activity, and AI
// Insights — all computed from real data (products, invoices, team
// members, leads) by GET /company-home/summary.
const HOME_SUMMARY_ENDPOINT = `${GROWTHOS_BASE}/company-home/summary`;

const initials = (name) =>
    (name || "?").trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase()).join("") || "?";

// Turns a MySQL timestamp string into "2 hours ago" / "Yesterday" / etc.
// for the Recent Activity feed. Falls back to "" for anything unparsable
// rather than showing "Invalid Date".
const timeAgo = (value) => {
    if (!value) return "";
    const then = new Date(value.replace(" ", "T"));
    if (Number.isNaN(then.getTime())) return "";
    const diffMs = Date.now() - then.getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins} min${mins === 1 ? "" : "s"} ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
    const days = Math.floor(hours / 24);
    if (days === 1) return "Yesterday";
    if (days < 7) return `${days} days ago`;
    return then.toLocaleDateString(undefined, { day: "numeric", month: "short" });
};

// Icon per Recent Activity event `type`, returned by /company-home/summary.
const ACTIVITY_ICON_BY_TYPE = {
    product: Package,
    payment: IndianRupee,
    team: UserPlus,
    notification: Megaphone,
};

// Login.jsx already stores the full company object under this key right
// after a successful login — used as the immediate, reliable source for
// this page so it never renders the empty "Welcome" fallback just because
// a background /auth/me refresh is slow or fails.
const readStoredCompany = () => {
    try {
        const raw = localStorage.getItem("growthos_company");
        return raw ? JSON.parse(raw) : null;
    } catch (err) {
        return null;
    }
};

/* ---------- small presentational pieces, kept in this same file ---------- */

// A quiet orange wireframe skyline for the hero card's right side — built
// from plain rects/lines, not a stock illustration, and kept subtle.
function SkylineIllustration() {
    return (
        <svg
            viewBox="0 0 420 300"
            className="pointer-events-none absolute -right-6 bottom-0 h-full w-[70%] max-w-[440px] opacity-[0.35] [mask-image:linear-gradient(to_left,black_35%,transparent_92%)]"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <defs>
                <linearGradient id="skylineStroke" x1="0" y1="0" x2="0" y2="300" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stopColor="#FF8A3D" />
                    <stop offset="100%" stopColor="#FF6B00" stopOpacity="0.35" />
                </linearGradient>
            </defs>
            <ellipse cx="300" cy="120" rx="150" ry="46" stroke="url(#skylineStroke)" strokeWidth="1" opacity="0.5" />
            <ellipse cx="300" cy="120" rx="110" ry="150" stroke="url(#skylineStroke)" strokeWidth="1" opacity="0.35" />
            {[
                [230, 150, 34, 150],
                [268, 110, 40, 190],
                [312, 70, 44, 230],
                [360, 130, 30, 170],
            ].map(([x, y, w, h], i) => (
                <g key={i}>
                    <rect x={x} y={y} width={w} height={h} rx="3" stroke="url(#skylineStroke)" strokeWidth="1.1" />
                    {Array.from({ length: Math.floor(h / 18) }).map((_, r) => (
                        <line key={r} x1={x} y1={y + 14 + r * 18} x2={x + w} y2={y + 14 + r * 18} stroke="url(#skylineStroke)" strokeWidth="0.6" opacity="0.5" />
                    ))}
                </g>
            ))}
            <line x1="180" y1="300" x2="420" y2="300" stroke="url(#skylineStroke)" strokeWidth="1" opacity="0.4" />
        </svg>
    );
}

const CompanyInfoTile = ({ icon: Icon, label, value }) => {
    if (!value) return null;
    return (
        <div className="group flex items-center gap-3 rounded-2xl border border-white/[0.08] bg-white/[0.03] px-4 py-3.5 backdrop-blur-xl transition hover:border-orange-500/40 hover:bg-orange-500/[0.05]">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-orange-500/20 to-orange-600/5 text-orange-400 ring-1 ring-orange-500/25 transition group-hover:from-orange-400 group-hover:to-orange-600 group-hover:text-white">
                <Icon size={15} />
            </span>
            <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-white">{value}</p>
                <p className="text-[11px] text-zinc-500">{label}</p>
            </div>
        </div>
    );
};

const StatCard = ({ icon: Icon, value, label, description, accent, onClick }) => {
    const Tag = onClick ? "button" : "div";
    return (
        <Tag
            type={onClick ? "button" : undefined}
            onClick={onClick}
            className={`group relative w-full overflow-hidden rounded-xl border border-white/[0.07] bg-white/[0.015] p-5 text-left transition-colors duration-200 ${
                onClick ? "cursor-pointer hover:border-white/[0.16] hover:bg-white/[0.03]" : ""
            }`}
        >
            <span className="pointer-events-none absolute left-0 top-0 h-full w-[2px] origin-top scale-y-0 bg-orange-500 transition-transform duration-300 ease-out group-hover:scale-y-100" />
            <Icon size={15} strokeWidth={2} className="text-zinc-600 transition-colors group-hover:text-orange-400" />
            <p className="mt-4 text-[26px] font-extrabold leading-none tracking-tight text-white">{value}</p>
            <p className="mt-2 text-[13px] font-medium text-zinc-400">{label}</p>
            <p className={`mt-0.5 text-[11px] ${accent ? "font-medium text-orange-400" : "text-zinc-600"}`}>{description}</p>
        </Tag>
    );
};

const SectionEyebrow = ({ children }) => (
    <div className="mb-4 flex items-center gap-3">
        <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-600">{children}</span>
        <span className="h-px flex-1 bg-white/[0.06]" />
    </div>
);

// 6 shortcut buttons. Only "Add Product" is wired to a real handler —
// the rest navigate to routes matching your sidebar's naming. Update
// `path` for any that differ from your actual react-router routes.
const QUICK_ACTIONS = [
    { icon: Plus, label: "Add Product", subtitle: "List a new product", key: "add-product" },
    { icon: Target, label: "Generate Leads", subtitle: "Find new prospects", path: "/analytics" },
    { icon: Megaphone, label: "Campaign Builder", subtitle: "Launch a campaign", path: "/campaign-builder" },
    { icon: UserPlus, label: "Invite Team", subtitle: "Add a teammate", path: "/team" },
    { icon: Rocket, label: "Upgrade Plan", subtitle: "Unlock more limits", path: "/billing" },
    { icon: Bot, label: "Open AI Assistant", subtitle: "Ask GrowthOS AI", path: "/ai-command-center" },
];

// Recent Activity now comes from GET /company-home/summary (real products,
// payments, team invites, and notifications merged server-side) — see
// `activity` state below instead of a static list.

const InsightRow = ({ icon: Icon, label, value }) => (
    <div className="flex items-start gap-3 rounded-2xl border border-white/[0.08] bg-white/[0.03] p-4 transition hover:border-orange-500/40 hover:bg-orange-500/[0.06]">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-orange-500/20 to-orange-600/5 text-orange-400 ring-1 ring-orange-500/25">
            <Icon size={14} />
        </span>
        <div className="min-w-0">
            <p className="text-xs font-medium text-zinc-500">{label}</p>
            <p className="mt-0.5 text-sm font-semibold text-white">{value}</p>
        </div>
    </div>
);

/* ---------------------------- main component ---------------------------- */

const Home = () => {
    const navigate = useNavigate();
    const routerLocation = useLocation();

    const [company, setCompany] = useState(() => readStoredCompany());
    const [companyLoading, setCompanyLoading] = useState(false);

    // Overview stats + Recent Activity + AI Insights, all from the one
    // real-data-backed endpoint below (GET /company-home/summary).
    const [homeSummary, setHomeSummary] = useState(null);
    const [homeSummaryLoading, setHomeSummaryLoading] = useState(true);

    const fetchCompany = async () => {
        setCompanyLoading(true);
        try {
            const res = await fetch(COMPANY_ENDPOINT, { method: "GET", headers: { ...authHeaders() } });
            if (res.status === 401) {
                navigate("/login");
                return;
            }
            const resp = await res.json();
            if (res.ok && resp.success && resp.data) {
                setCompany(resp.data);
            }
        } catch (err) {
            // Network hiccup — silently keep the existing company data.
        } finally {
            setCompanyLoading(false);
        }
    };

    const fetchHomeSummary = async () => {
        setHomeSummaryLoading(true);
        try {
            const res = await fetch(HOME_SUMMARY_ENDPOINT, { method: "GET", headers: { ...authHeaders() } });
            if (res.status === 401) {
                navigate("/login");
                return;
            }
            const resp = await res.json();
            setHomeSummary(res.ok && resp.success ? resp : null);
        } catch (err) {
            // Network hiccup — Overview/Activity/Insights just show their
            // empty states below rather than fake numbers.
            setHomeSummary(null);
        } finally {
            setHomeSummaryLoading(false);
        }
    };

    useEffect(() => {
        if (!localStorage.getItem("growthos_token")) {
            navigate("/login");
            return;
        }
        fetchCompany();
        fetchHomeSummary();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Product add/edit now lives on the dedicated Products page.
    const handleQuickAction = (action) => () => {
        if (action.key === "add-product") {
            navigate("/company_product?add=1");
        } else if (action.path) {
            navigate(action.path);
        }
    };

    const location = [company?.address_city, company?.address_state, company?.address_country]
        .filter(Boolean)
        .join(", ");

    // Not part of the current company API yet — shown only when present,
    // so this stays honest rather than fabricating GST/founding info.
    const gstLabel = company?.gst_status || company?.gstin ? "GST Registered" : null;
    const since = company?.founded_year || company?.company_since || null;

    // Overview stat tiles + activity feed, sourced from /company-home/summary.
    const stats = homeSummary?.stats || null;
    const activity = homeSummary?.recent_activity || [];
    const insights = homeSummary?.insights || null;
    const topProduct = insights?.top_product || "Not enough data yet";
    const lowEngagement = insights?.low_engagement || "None flagged this week";

    // Business Workspace is now a separate view within Home, not a section
    // you scroll past — it only shows when the sidebar's "Business Workspace"
    // link is clicked (which sets this hash), and hides the rest of Home
    // while it's open.
    const showBusinessWorkspace = routerLocation.hash === "#business-workspace";

    return (
        <div className="relative min-h-screen w-full bg-[#050505] text-zinc-300">
            <Sidebar />

            <div className="relative min-h-screen w-full overflow-hidden lg:pl-64">
                <Starfield />

                <div className="relative z-10 mx-auto max-w-7xl space-y-8 px-4 py-10 sm:px-6 sm:py-14 lg:px-10">
                {showBusinessWorkspace ? (
                <div>
                    {/* Back to the normal Home view — clears the hash */}
                    <button
                        type="button"
                        onClick={() => navigate("/company_home")}
                        className="mb-5 inline-flex items-center gap-1.5 text-sm font-medium text-zinc-500 transition duration-200 hover:text-orange-300"
                    >
                        ← Back to Home
                    </button>
                    <SectionEyebrow>Business Workspace</SectionEyebrow>
                    <BusinessWorkspaceSection />
                </div>
                ) : (
                <>
                {/* Section 1 — Company hero card */}
                {companyLoading ? (
                    <div className="flex items-center gap-2 text-sm text-zinc-500">
                        <Loader2 size={14} className="animate-spin" /> Loading company info...
                    </div>
                ) : company ? (
                    <div className="relative overflow-hidden rounded-[28px] border border-white/[0.08] bg-white/[0.03] p-6 shadow-[0_0_60px_-24px_rgba(255,107,0,0.28)] backdrop-blur-2xl sm:p-9">
                        <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-orange-500/60 to-transparent" />
                        <SkylineIllustration />

                        <div className="relative flex flex-col gap-7 lg:flex-row lg:items-start lg:justify-between">
                            <div className="flex min-w-0 items-start gap-5">
                                <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-orange-400 to-orange-600 text-2xl font-extrabold text-white shadow-[0_0_30px_-6px_rgba(255,107,0,0.6)] ring-1 ring-orange-400/40">
                                    {initials(company.company_name)}
                                </div>
                                <div className="min-w-0">
                                    <p className="flex items-center gap-1.5 text-xs font-semibold tracking-wide text-zinc-400">
                                        👋 Welcome back,
                                    </p>
                                    <h1 className="mt-1 truncate text-3xl font-extrabold leading-tight tracking-tight text-white sm:text-4xl">
                                        {company.company_name?.split(" ").slice(0, -1).join(" ")}{" "}
                                        <span className="text-orange-500">{company.company_name?.split(" ").slice(-1)}</span>
                                    </h1>

                                    <div className="mt-3 flex flex-wrap items-center gap-2">
                                        {company.industry_sector && (
                                            <span className="inline-flex items-center gap-1.5 rounded-full border border-orange-500/25 bg-orange-500/10 px-3 py-1 text-xs font-medium text-orange-300">
                                                <Briefcase size={12} /> {company.industry_sector}
                                            </span>
                                        )}
                                        {company.company_type && (
                                            <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-medium text-zinc-300">
                                                <Building2 size={12} /> {company.company_type}
                                            </span>
                                        )}
                                        {gstLabel && (
                                            <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/25 bg-emerald-500/10 px-3 py-1 text-xs font-medium text-emerald-300">
                                                <ShieldCheck size={12} /> {gstLabel}
                                            </span>
                                        )}
                                        {since && (
                                            <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-medium text-zinc-400">
                                                <CalendarDays size={12} /> Since {since}
                                            </span>
                                        )}
                                    </div>

                                    {company.description && (
                                        <p className="mt-3 max-w-md text-sm leading-relaxed text-zinc-500">{company.description}</p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {(company.email || company.contact_number || company.website || location) && (
                            <div className="relative mt-7 border-t border-white/[0.07] pt-6">
                                <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-600">Contact</p>
                                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                                    <CompanyInfoTile icon={Mail} label="Email Address" value={company.email} />
                                    <CompanyInfoTile icon={Phone} label="Phone Number" value={company.contact_number} />
                                    <CompanyInfoTile icon={Globe} label="Website" value={company.website} />
                                    <CompanyInfoTile icon={MapPin} label="Location" value={location} />
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <h1 className="text-2xl font-extrabold text-white sm:text-3xl">Welcome to GrowthOS AI</h1>
                )}

                {/* Section 2 — Company statistics */}
                <div>
                    <SectionEyebrow>Overview</SectionEyebrow>
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                        <StatCard
                            icon={Package}
                            value={homeSummaryLoading ? "…" : stats?.products_count ?? 0}
                            label="Products"
                            description="Active"
                            onClick={() => navigate("/company_product")}
                        />
                        {/* Billing/subscriptions aren't wired up on the backend yet, so
                            rather than show a fake plan, this stays honest until a real
                            billing system exists to back it — see /company-home/summary. */}
                        <StatCard
                            icon={Crown}
                            value={stats?.plan_name || "Not set"}
                            label="Current Plan"
                            description={stats?.plan_name ? "" : "No billing plan yet"}
                        />
                        <StatCard
                            icon={CalendarClock}
                            value={stats?.plan_expiry || "—"}
                            label="Plan Expiry"
                            description={stats?.plan_expiry ? "" : "Not applicable"}
                        />
                        <StatCard
                            icon={Users}
                            value={homeSummaryLoading ? "…" : stats?.team_members_active ?? 0}
                            label="Team Members"
                            description={
                                homeSummaryLoading
                                    ? ""
                                    : stats?.team_members_invited
                                    ? `+${stats.team_members_invited} invited`
                                    : "Active"
                            }
                            onClick={() => navigate("/team")}
                        />
                        <StatCard
                            icon={IndianRupee}
                            value={homeSummaryLoading ? "…" : `₹${(stats?.monthly_revenue ?? 0).toLocaleString("en-IN")}`}
                            label="Monthly Revenue"
                            description={
                                homeSummaryLoading || stats?.revenue_change_percent == null
                                    ? "This month"
                                    : `${stats.revenue_change_percent >= 0 ? "+" : ""}${stats.revenue_change_percent}% vs last month`
                            }
                            accent={stats?.revenue_change_percent > 0}
                        />
                    </div>
                </div>


                {/* Section 3 — Quick actions */}
                <div>
                    <SectionEyebrow>Quick Actions</SectionEyebrow>
                    <div className="flex flex-wrap gap-2">
                        {QUICK_ACTIONS.map((action) => (
                            <button
                                key={action.label}
                                type="button"
                                onClick={handleQuickAction(action)}
                                title={action.subtitle}
                                className="group flex items-center gap-2.5 rounded-full border border-white/[0.08] bg-white/[0.02] py-2.5 pl-3 pr-4 transition-colors duration-200 hover:border-orange-500/40 hover:bg-orange-500/[0.06]"
                            >
                                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white/[0.05] text-zinc-400 transition-colors duration-200 group-hover:bg-orange-500 group-hover:text-white">
                                    <action.icon size={13} />
                                </span>
                                <span className="text-[13px] font-semibold text-white">{action.label}</span>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Section 4 — Recent activity + AI insights */}
                <div>
                <SectionEyebrow>Activity &amp; Insights</SectionEyebrow>
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
                    <div className="relative overflow-hidden rounded-[24px] border border-white/[0.08] bg-white/[0.03] p-6 backdrop-blur-2xl lg:col-span-2">
                        <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-orange-500/50 to-transparent" />
                        <h2 className="flex items-center gap-2 text-lg font-bold text-white">
                            <History size={18} className="text-orange-500" /> Recent Activity
                        </h2>
                        {homeSummaryLoading ? (
                            <div className="mt-6 flex items-center gap-2 text-sm text-zinc-500">
                                <Loader2 size={14} className="animate-spin" /> Loading activity...
                            </div>
                        ) : activity.length === 0 ? (
                            <p className="mt-6 text-sm text-zinc-500">
                                No activity yet — add a product or invite a teammate to get started.
                            </p>
                        ) : (
                            <ul className="relative mt-6 space-y-6 before:absolute before:left-[15px] before:top-1 before:h-[calc(100%-8px)] before:w-px before:bg-white/[0.08]">
                                {activity.map((item, i) => {
                                    const ItemIcon = ACTIVITY_ICON_BY_TYPE[item.type] || History;
                                    return (
                                        <li key={i} className="relative flex items-start gap-4">
                                            <span className="relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-orange-500/25 bg-[#0a0a0a] text-orange-400">
                                                <ItemIcon size={14} />
                                            </span>
                                            <div className="min-w-0 pt-1">
                                                <p className="text-sm font-semibold text-white">{item.text}</p>
                                                <p className="truncate text-xs text-zinc-500">{item.detail}</p>
                                            </div>
                                            <span className="ml-auto shrink-0 pt-1 text-[11px] text-zinc-600">{timeAgo(item.timestamp)}</span>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>

                    <div className="relative overflow-hidden rounded-[24px] border border-orange-500/25 bg-gradient-to-br from-orange-500/[0.07] via-white/[0.03] to-white/[0.03] p-6 shadow-[0_0_50px_-20px_rgba(255,107,0,0.35)] backdrop-blur-2xl lg:col-span-3">
                        <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-orange-400/70 to-transparent" />
                        <h2 className="flex items-center gap-2 text-lg font-bold text-white">
                            <Sparkles size={18} className="text-orange-500" /> AI Insights
                        </h2>
                        <p className="mt-0.5 text-xs text-zinc-500">Generated from your products and pipeline activity</p>

                        {homeSummaryLoading ? (
                            <div className="mt-5 flex items-center gap-2 text-sm text-zinc-500">
                                <Loader2 size={14} className="animate-spin" /> Generating insights...
                            </div>
                        ) : (
                            <>
                                <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                                    <InsightRow icon={TrendingUp} label="Top performing product" value={topProduct} />
                                    <InsightRow icon={Sparkles} label="Revenue" value={insights?.revenue_insight || "Not enough billing data yet"} />
                                    <InsightRow icon={Megaphone} label="Recommended campaign" value={insights?.recommended_campaign || "Not enough data yet"} />
                                    <InsightRow icon={AlertTriangle} label="Low engagement" value={lowEngagement} />
                                </div>

                                <div className="mt-3 flex items-start gap-3 rounded-2xl border border-white/[0.08] bg-white/[0.03] p-4">
                                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-orange-500/20 to-orange-600/5 text-orange-400 ring-1 ring-orange-500/25">
                                        <Lightbulb size={14} />
                                    </span>
                                    <div>
                                        <p className="text-xs font-medium text-zinc-500">Suggested improvement</p>
                                        <p className="mt-0.5 text-sm font-semibold text-white">
                                            {insights?.suggested_improvement ||
                                                "Add a target audience to products missing one — it sharpens who your next campaign reaches."}
                                        </p>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
                </div>
                </>
                )}
                </div>
            </div>

        </div>
    );
};

export default Home;